/**
 * ArtifactStatusBadge — small pill badge showing an artifact's edit status.
 * See specs/phase2/CR-D-artifact-editing-frontend-spec.md §3.2.
 */

import { cn } from "@/lib/utils";
import type { ArtifactStatus } from "@/hooks/useArtifacts";

interface StatusStyle {
  label: string;
  className: string;
}

const STATUS_STYLES: Record<ArtifactStatus, StatusStyle> = {
  generated: { label: "Generated", className: "bg-gray-100 text-gray-600" },
  draft_edit: { label: "Draft", className: "bg-blue-100 text-blue-700" },
  pending_commit: {
    label: "Pending Approval",
    className: "bg-orange-100 text-orange-700",
  },
  committed: { label: "Committed", className: "bg-green-100 text-green-700" },
  rejected: {
    label: "Changes Requested",
    className: "bg-red-100 text-red-700",
  },
};

export interface ArtifactStatusBadgeProps {
  status: ArtifactStatus;
  className?: string;
}

export function ArtifactStatusBadge({
  status,
  className,
}: ArtifactStatusBadgeProps): JSX.Element {
  const { label, className: statusClassName } = STATUS_STYLES[status];

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
        statusClassName,
        className,
      )}
    >
      {label}
    </span>
  );
}

export default ArtifactStatusBadge;
